import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Chip, Drawer, IconButton, Collapse,
  TextField, FormControl, InputLabel, Select, MenuItem, Button, Divider, Link,
  CircularProgress, Alert, Snackbar,
} from '@mui/material';
import CloseIcon       from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon   from '@mui/icons-material/OpenInNew';
import LockIcon        from '@mui/icons-material/Lock';
import UploadFileIcon  from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon    from '@mui/icons-material/Download';

import {
  getRequirements, updateRequirement,
  getCurrentSsp, getSspHistory, uploadSsp, deleteSsp, sspDownloadUrl,
  parseSsp, applySsp, exportSsp,
  getApplicableStigs,
} from '../api/odp';
import SspDiffDialog from '../components/SspDiffDialog';
import SspExportDialog from '../components/SspExportDialog';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';

const ALLOWED_EXT = ['.pdf', '.docx', '.xlsx'];

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Status enum (with audit-friendly colors) ────────────────────
const STATUS = [
  { val: 'compliant',     label: 'Compliant',     color: '#4caf50' },
  { val: 'partial',       label: 'Partial',       color: '#ffeb3b' },
  { val: 'non_compliant', label: 'Non-Compliant', color: '#f44336' },
  { val: 'na',            label: 'N/A',           color: '#7a8fa6' },
  { val: 'not_assessed',  label: 'Not Assessed',  color: '#4a5a70' },
];
const STATUS_LABEL = Object.fromEntries(STATUS.map(s => [s.val, s.label]));
const STATUS_COLOR = Object.fromEntries(STATUS.map(s => [s.val, s.color]));

// ── Categories ──────────────────────────────────────────────────
const CATEGORIES = [
  'Insider Threat',
  'SIEM / Continuous Monitoring',
  'Audit & Accountability',
  'COOP / Disaster Recovery',
  'Access Control',
  'Configuration Management',
  'Incident Response',
  'Risk Management',
  'System & Information Integrity',
  'Identification & Authentication',
];


// ── Status chip ────────────────────────────────────────────────
function StatusChip({ value }) {
  const color = STATUS_COLOR[value] || '#7a8fa6';
  return (
    <Chip
      label={STATUS_LABEL[value] || value}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        color,
        backgroundColor: `${color}22`,
        border: `1px solid ${color}55`,
      }}
    />
  );
}

// ── Requirement row ────────────────────────────────────────────
function RequirementRow({ item, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr 140px 28px',
        gap: 1.5,
        alignItems: 'center',
        padding: '10px 14px',
        borderBottom: '1px solid #1a2840',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#0d1f33' },
      }}
    >
      <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>
        {item.reference}
      </Typography>
      <Box>
        <Typography sx={{ color: '#c0cfe0', fontSize: 13, fontWeight: 600 }}>{item.title}</Typography>
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.requirement}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <StatusChip value={item.status} />
      </Box>
      <Typography sx={{ color: item.evidence_url ? '#4caf50' : '#2a3a50', fontSize: 16, textAlign: 'center' }} title={item.evidence_url ? 'Evidence linked' : 'No evidence'}>
        {item.evidence_url ? '●' : '○'}
      </Typography>
    </Box>
  );
}

// ── Category section ───────────────────────────────────────────
function CategorySection({ category, items, onSelect }) {
  const [open, setOpen] = useState(true);
  const counts = useMemo(() => {
    const c = { compliant: 0, partial: 0, non_compliant: 0, na: 0, not_assessed: 0 };
    items.forEach(i => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, mb: 2, overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: open ? '1px solid #1e3a5f' : 'none',
          cursor: 'pointer', backgroundColor: '#0d1825',
          '&:hover': { backgroundColor: '#0d2137' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {open ? <KeyboardArrowDownIcon sx={{ color: '#00bcd4', fontSize: 18 }} /> : <KeyboardArrowRightIcon sx={{ color: '#7a8fa6', fontSize: 18 }} />}
          <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 14 }}>{category}</Typography>
          <Chip label={`${items.length}`} size="small" sx={{ height: 18, fontSize: 10, backgroundColor: '#1e3a5f', color: '#7a8fa6' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {STATUS.map(s => counts[s.val] > 0 && (
            <Chip
              key={s.val}
              label={`${counts[s.val]} ${s.label}`}
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 700, color: s.color, backgroundColor: `${s.color}22`, border: `1px solid ${s.color}44` }}
            />
          ))}
        </Box>
      </Box>
      <Collapse in={open}>
        {items.map(it => <RequirementRow key={it.id} item={it} onClick={() => onSelect(it)} />)}
      </Collapse>
    </Paper>
  );
}

// ── Applicable STIGs (via CCI) ─────────────────────────────────
function ApplicableStigs({ reqId, systemId }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    getApplicableStigs(reqId, systemId)
      .then(d => { if (!cancelled) setState({ loading: false, data: d, error: null }); })
      .catch(err => { if (!cancelled) setState({ loading: false, data: null, error: err.message }); });
    return () => { cancelled = true; };
  }, [reqId, systemId]);

  if (state.loading) {
    return (
      <Box>
        <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Applicable STIGs</Typography>
        <CircularProgress size={16} sx={{ color: '#00bcd4' }} />
      </Box>
    );
  }
  if (state.error) {
    return (
      <Box>
        <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Applicable STIGs</Typography>
        <Typography sx={{ color: '#f44336', fontSize: 11 }}>{state.error}</Typography>
      </Box>
    );
  }
  const d = state.data;
  if (!d?.nist_id) {
    return (
      <Box>
        <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Applicable STIGs</Typography>
        <Typography sx={{ color: '#4a5a70', fontSize: 11, fontStyle: 'italic' }}>
          {d?.note || 'No CCI mapping — manual review.'}
        </Typography>
      </Box>
    );
  }
  if (!d.stigs?.length) {
    return (
      <Box>
        <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
          Applicable STIGs — {d.nist_id}
        </Typography>
        <Typography sx={{ color: '#4a5a70', fontSize: 11, fontStyle: 'italic' }}>
          No STIGs in the library reference CCIs covering this control yet.
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
        Applicable STIGs — {d.nist_id} <Box component="span" sx={{ color: '#4a5a70' }}>({d.stigs.length})</Box>
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {d.stigs.map(s => (
          <Box key={s.stig_id} sx={{
            padding: '8px 10px', backgroundColor: '#0a1525', borderRadius: 1, border: '1px solid #1e3a5f',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Typography sx={{ color: '#c0cfe0', fontSize: 12, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.stig_title}
              </Typography>
              {s.applied_assets > 0 && (
                <Chip
                  label={`${s.applied_assets} asset${s.applied_assets === 1 ? '' : 's'}`}
                  size="small"
                  sx={{ height: 16, fontSize: 9, fontWeight: 700, color: '#4caf50', backgroundColor: '#4caf5022', border: '1px solid #4caf5044' }}
                />
              )}
            </Box>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, fontFamily: 'monospace' }}>
              covers via {s.ccis.slice(0, 4).join(', ')}{s.ccis.length > 4 ? `, +${s.ccis.length - 4}` : ''}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Detail drawer ──────────────────────────────────────────────
function DetailDrawer({ item, systemId, onClose, onSave, canEdit }) {
  const [form, setForm] = useState(item);
  if (!item) return null;
  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
      color: '#c0cfe0', fontSize: 13,
      '& fieldset': { borderColor: '#1e3a5f' },
      '&:hover fieldset': { borderColor: '#00bcd4' },
      '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
    },
    '& .MuiInputLabel-root': { color: '#7a8fa6', fontSize: 13 },
    '& .MuiInputLabel-root.Mui-focused': { color: '#00bcd4' },
    '& .MuiSelect-icon': { color: '#7a8fa6' },
  };

  const handleSave = () => {
    onSave(form);  // parent does the server PATCH + state merge
    onClose();
  };

  return (
    <Drawer anchor="right" open={Boolean(item)} onClose={onClose}
      PaperProps={{ sx: { width: 480, backgroundColor: '#0d1825', borderLeft: '1px solid #1e3a5f' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1e3a5f' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>
            {item.reference}
          </Typography>
          <Typography sx={{ color: '#ffffff', fontSize: 15, fontWeight: 700, mt: 0.25 }}>{item.title}</Typography>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, mt: 0.25 }}>{item.category}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#7a8fa6' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto' }}>
        <Box>
          <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Requirement</Typography>
          <Typography sx={{ color: '#c0cfe0', fontSize: 13, lineHeight: 1.6 }}>{item.requirement}</Typography>
        </Box>

        <ApplicableStigs reqId={item.id} systemId={systemId} />

        <Divider sx={{ borderColor: '#1e3a5f' }} />

        {!canEdit && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '8px 10px', backgroundColor: '#0a1525', borderRadius: 1, border: '1px solid #1e3a5f' }}>
            <LockIcon sx={{ fontSize: 14, color: '#7a8fa6' }} />
            <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>Read-only view — auditor mode</Typography>
          </Box>
        )}

        <FormControl size="small" fullWidth sx={FIELD_SX} disabled={!canEdit}>
          <InputLabel>Compliance Status</InputLabel>
          <Select value={form.status} onChange={set('status')} label="Compliance Status"
            MenuProps={{ PaperProps: { sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } } }}>
            {STATUS.map(s => (
              <MenuItem key={s.val} value={s.val} sx={{ color: s.color, fontSize: 13 }}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Evidence URL"
          value={form.evidence_url}
          onChange={set('evidence_url')}
          size="small"
          fullWidth
          disabled={!canEdit}
          placeholder="https://policy.aspyn.mil/insider-threat.pdf"
          sx={FIELD_SX}
        />
        {form.evidence_url && (
          <Link href={form.evidence_url} target="_blank" rel="noopener" sx={{ color: '#00bcd4', fontSize: 12, display: 'flex', alignItems: 'center', gap: 0.5, mt: -1.5 }}>
            <OpenInNewIcon sx={{ fontSize: 13 }} /> Open evidence
          </Link>
        )}

        <TextField
          label="Notes / Auditor Response"
          value={form.notes}
          onChange={set('notes')}
          size="small"
          fullWidth
          multiline
          rows={5}
          disabled={!canEdit}
          placeholder="How this requirement is satisfied, who is responsible, any gaps or POA&M items..."
          sx={FIELD_SX}
        />

        {(item.reviewed_at || item.reviewed_by) && (
          <Box sx={{ padding: '8px 10px', backgroundColor: '#0a1525', borderRadius: 1, border: '1px solid #1e3a5f' }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Last Reviewed</Typography>
            <Typography sx={{ color: '#c0cfe0', fontSize: 12, mt: 0.25 }}>
              {item.reviewed_at || '—'}{item.reviewed_by ? ` · ${item.reviewed_by}` : ''}
            </Typography>
          </Box>
        )}

        {canEdit && (
          <>
            <Divider sx={{ borderColor: '#1e3a5f' }} />
            <Button
              variant="contained"
              onClick={handleSave}
              fullWidth
              sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, fontSize: 13, '&:hover': { backgroundColor: '#00acc1' } }}
            >
              Save
            </Button>
          </>
        )}
      </Box>
    </Drawer>
  );
}

// ── Export history (collapsible list under the banner) ────────────
function ExportHistory({ history, systemId }) {
  const exports = history.filter(h => h.kind === 'export');
  if (exports.length === 0) return null;
  return (
    <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: 1.5, mb: 3 }}>
      <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <HistoryIcon sx={{ fontSize: 14 }} />
        Generated SSP versions ({exports.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {exports.map(e => (
          <Box key={e.uuid} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, padding: '6px 8px', borderRadius: 1, '&:hover': { backgroundColor: '#0d1f33' } }}>
            <Chip
              label={`v${e.version_number}`}
              size="small"
              sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: '#00bcd422', color: '#00bcd4', border: '1px solid #00bcd455', minWidth: 36 }}
            />
            <Chip
              label={(e.export_format || '').toUpperCase()}
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 700, backgroundColor: '#1e3a5f', color: '#7a8fa6' }}
            />
            <Typography sx={{ color: '#c0cfe0', fontSize: 12, flex: 1, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {e.filename}
            </Typography>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>
              signed by {e.signed_by || '—'} · {new Date(e.uploaded_at).toLocaleString()}
            </Typography>
            <Button
              component="a"
              href={sspDownloadUrl(e.uuid, systemId)}
              target="_blank"
              rel="noopener"
              size="small"
              startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />}
              sx={{ color: '#00bcd4', fontSize: 11, minWidth: 0 }}
            >
              Download
            </Button>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

// ── SSP banner ─────────────────────────────────────────────────
function SspBanner({ systemId, ssp, canEdit, busy, parsing, exporting, onPickFile, onDelete, onParse, onExport }) {
  if (!ssp) {
    return (
      <Paper sx={{
        backgroundColor: '#111827', border: '1px dashed #1e3a5f', borderRadius: 2,
        padding: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DescriptionIcon sx={{ color: '#4a5a70', fontSize: 24 }} />
          <Box>
            <Typography sx={{ color: '#c0cfe0', fontSize: 13, fontWeight: 600 }}>
              No SSP attached
            </Typography>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>
              Upload your System Security Plan (PDF, DOCX, or XLSX) so auditors can review the source document.
            </Typography>
          </Box>
        </Box>
        {canEdit && (
          <Button
            variant="outlined"
            size="small"
            startIcon={busy ? <CircularProgress size={14} sx={{ color: '#00bcd4' }} /> : <UploadFileIcon sx={{ fontSize: 16 }} />}
            disabled={busy}
            onClick={onPickFile}
            sx={{
              color: '#00bcd4', borderColor: '#00bcd4', fontWeight: 700, fontSize: 12,
              '&:hover': { borderColor: '#00bcd4', backgroundColor: '#00bcd411' },
              '&.Mui-disabled': { color: '#4a5a70', borderColor: '#1e3a5f' },
            }}
          >
            {busy ? 'Uploading…' : 'Upload SSP'}
          </Button>
        )}
      </Paper>
    );
  }

  return (
    <Paper sx={{
      backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2,
      padding: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        <DescriptionIcon sx={{ color: '#00bcd4', fontSize: 24, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#ffffff', fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ssp.filename}
          </Typography>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>
            {formatBytes(ssp.size_bytes)} · uploaded by {ssp.uploaded_by} · {new Date(ssp.uploaded_at).toLocaleString()}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
        <Button
          component="a"
          href={sspDownloadUrl(ssp.uuid, systemId)}
          target="_blank"
          rel="noopener"
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          sx={{ color: '#00bcd4', borderColor: '#00bcd4', fontSize: 11, '&:hover': { borderColor: '#00bcd4', backgroundColor: '#00bcd411' } }}
        >
          Download
        </Button>
        {canEdit && (
          <>
            {ssp.filename.toLowerCase().endsWith('.xlsx') && (
              <Button
                variant="outlined"
                size="small"
                startIcon={parsing ? <CircularProgress size={12} sx={{ color: '#00bcd4' }} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
                disabled={busy || parsing || exporting}
                onClick={onParse}
                sx={{ color: '#00bcd4', borderColor: '#00bcd4', fontSize: 11, '&:hover': { borderColor: '#00bcd4', backgroundColor: '#00bcd411' } }}
              >
                {parsing ? 'Parsing…' : 'Parse & Map'}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={exporting ? <CircularProgress size={12} sx={{ color: '#0a0e1a' }} /> : <FileDownloadIcon sx={{ fontSize: 16 }} />}
              disabled={busy || parsing || exporting}
              onClick={onExport}
              sx={{
                backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, fontSize: 11,
                '&:hover': { backgroundColor: '#00acc1' },
                '&.Mui-disabled': { backgroundColor: '#1e3a5f', color: '#4a5a70' },
              }}
            >
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={busy ? <CircularProgress size={12} sx={{ color: '#00bcd4' }} /> : <UploadFileIcon sx={{ fontSize: 16 }} />}
              disabled={busy || parsing || exporting}
              onClick={onPickFile}
              sx={{ color: '#7a8fa6', borderColor: '#1e3a5f', fontSize: 11, '&:hover': { color: '#00bcd4', borderColor: '#00bcd4', backgroundColor: '#00bcd411' } }}
            >
              Replace
            </Button>
            <IconButton onClick={onDelete} size="small" title="Remove SSP" disabled={parsing || exporting} sx={{ color: '#f44336', opacity: 0.7, '&:hover': { opacity: 1, backgroundColor: '#f4433611' } }}>
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </>
        )}
      </Box>
    </Paper>
  );
}

// ── Main page ──────────────────────────────────────────────────
export default function Odp({ systemId, currentUser }) {
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'issm';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [ssp, setSsp] = useState(null);
  const [history, setHistory] = useState([]);
  const [sspBusy, setSspBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [diff, setDiff] = useState(null);
  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' });
  const fileRef = useRef(null);

  const refreshHistory = async () => {
    try { setHistory(await getSspHistory(systemId)); } catch { /* non-fatal */ }
  };
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus]     = useState('all');
  const [selected, setSelected]             = useState(null);

  const totals = useMemo(() => {
    const t = { total: items.length, compliant: 0, partial: 0, non_compliant: 0, na: 0, not_assessed: 0 };
    items.forEach(i => { t[i.status] = (t[i.status] || 0) + 1; });
    return t;
  }, [items]);

  const filtered = useMemo(() => items.filter(i =>
    (filterCategory === 'all' || i.category === filterCategory) &&
    (filterStatus   === 'all' || i.status   === filterStatus)
  ), [items, filterCategory, filterStatus]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(i => {
      if (!map[i.category]) map[i.category] = [];
      map[i.category].push(i);
    });
    return CATEGORIES.filter(c => map[c]).map(c => [c, map[c]]);
  }, [filtered]);

  useEffect(() => {
    if (!systemId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    Promise.all([
      getRequirements(systemId).catch(e => { throw new Error(`requirements: ${e.message}`); }),
      getCurrentSsp(systemId).catch(() => null),  // 404 / null is fine
      getSspHistory(systemId).catch(() => []),
    ])
      .then(([rows, sspRecord, hist]) => {
        if (cancelled) return;
        setItems(rows);
        setSsp(sspRecord || null);
        setHistory(hist || []);
      })
      .catch(err => { if (!cancelled) setLoadError(err.message || 'Failed to load ODP data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [systemId]);

  const handleSave = async (updated) => {
    try {
      const saved = await updateRequirement(updated.id, systemId, {
        status:       updated.status,
        evidence_url: updated.evidence_url || '',
        notes:        updated.notes || '',
      });
      setItems(prev => prev.map(i => i.id === saved.id ? saved : i));
      setSnack({ open: true, severity: 'success', msg: `Updated ${saved.reference}` });
    } catch (err) {
      setSnack({ open: true, severity: 'error', msg: `Save failed: ${err.message}` });
    }
  };

  const handlePickFile = () => fileRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // allow re-uploading the same filename
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setSnack({ open: true, severity: 'error', msg: `Unsupported file type. Allowed: ${ALLOWED_EXT.join(', ')}` });
      return;
    }
    setSspBusy(true);
    try {
      const record = await uploadSsp(systemId, file);
      setSsp(record);
      await refreshHistory();
      setSnack({ open: true, severity: 'success', msg: `Uploaded ${record.filename}` });
    } catch (err) {
      setSnack({ open: true, severity: 'error', msg: `Upload failed: ${err.message}` });
    } finally {
      setSspBusy(false);
    }
  };

  const handleExport = async (format, signedBy) => {
    setExporting(true);
    try {
      const rec = await exportSsp(systemId, format, signedBy);
      await refreshHistory();
      setExportDialogOpen(false);
      setSnack({
        open: true, severity: 'success',
        msg: `Exported SSP v${rec.version_number} (${format.toUpperCase()}) — signed by ${signedBy}`,
      });
      // Auto-trigger download
      window.open(sspDownloadUrl(rec.uuid, systemId), '_blank');
    } catch (err) {
      setSnack({ open: true, severity: 'error', msg: `Export failed: ${err.message}` });
    } finally {
      setExporting(false);
    }
  };

  const handleParse = async () => {
    if (!ssp) return;
    setParsing(true);
    try {
      const result = await parseSsp(ssp.uuid, systemId);
      setDiff(result);
      if (result.summary.will_change === 0) {
        setSnack({ open: true, severity: 'info', msg: 'Parsed — no changes proposed' });
      }
    } catch (err) {
      setSnack({ open: true, severity: 'error', msg: `Parse failed: ${err.message}` });
    } finally {
      setParsing(false);
    }
  };

  const handleApply = async (ids) => {
    if (!ssp || !ids.length) return;
    setApplying(true);
    try {
      const result = await applySsp(ssp.uuid, systemId, ids);
      const fresh = await getRequirements(systemId);
      setItems(fresh);
      setDiff(null);
      setSnack({ open: true, severity: 'success', msg: `Applied ${result.applied} change${result.applied === 1 ? '' : 's'} from SSP` });
    } catch (err) {
      setSnack({ open: true, severity: 'error', msg: `Apply failed: ${err.message}` });
    } finally {
      setApplying(false);
    }
  };

  const handleDeleteSsp = async () => {
    if (!ssp) return;
    if (!window.confirm(`Remove SSP "${ssp.filename}"?`)) return;
    setSspBusy(true);
    try {
      await deleteSsp(ssp.uuid, systemId);
      setSsp(null);
      await refreshHistory();
      setSnack({ open: true, severity: 'success', msg: 'SSP removed' });
    } catch (err) {
      setSnack({ open: true, severity: 'error', msg: `Delete failed: ${err.message}` });
    } finally {
      setSspBusy(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>
          Orders, Directives & Policies
        </Typography>
        <Typography sx={{ color: '#7a8fa6', fontSize: 13, mt: 0.5 }}>
          CORA-aligned compliance register — track applicable DoD orders, NIST 800-53 controls, and supporting policy evidence per requirement.
          {!canEdit && ' Read-only mode (auditor).'}
        </Typography>
      </Box>

      <input
        ref={fileRef}
        type="file"
        accept={ALLOWED_EXT.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <SspBanner
        systemId={systemId}
        ssp={ssp}
        canEdit={canEdit}
        busy={sspBusy}
        parsing={parsing}
        exporting={exporting}
        onPickFile={handlePickFile}
        onDelete={handleDeleteSsp}
        onParse={handleParse}
        onExport={() => setExportDialogOpen(true)}
      />

      <ExportHistory history={history} systemId={systemId} />

      <SspDiffDialog
        open={Boolean(diff)}
        onClose={() => setDiff(null)}
        diff={diff}
        applying={applying}
        onApply={handleApply}
      />

      <SspExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        parent={ssp}
        items={items}
        exporting={exporting}
        onExport={handleExport}
      />

      {loadError && (
        <Alert severity="error" sx={{ mb: 2, backgroundColor: '#2a0a0a', color: '#f44336' }}>
          Failed to load: {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 6 }}>
          <CircularProgress size={32} sx={{ color: '#00bcd4' }} />
        </Box>
      ) : (<>

      {/* Stat cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2, mb: 3 }}>
        {[
          { label: 'Total',         val: totals.total,         color: '#c0cfe0' },
          { label: 'Compliant',     val: totals.compliant,     color: '#4caf50' },
          { label: 'Partial',       val: totals.partial,       color: '#ffeb3b' },
          { label: 'Non-Compliant', val: totals.non_compliant, color: '#f44336' },
          { label: 'N/A',           val: totals.na,            color: '#7a8fa6' },
          { label: 'Not Assessed',  val: totals.not_assessed,  color: '#4a5a70' },
        ].map(s => (
          <Paper key={s.label} sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: 2, textAlign: 'center' }}>
            <Typography sx={{ color: s.color, fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>{s.val}</Typography>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.5 }}>{s.label}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Category:</Typography>
          {['all', ...CATEGORIES].map(c => {
            const active = filterCategory === c;
            return (
              <Chip
                key={c}
                label={c === 'all' ? 'All' : c}
                size="small"
                onClick={() => setFilterCategory(c)}
                sx={{
                  fontSize: 11, fontWeight: active ? 700 : 400, cursor: 'pointer',
                  color: active ? '#00bcd4' : '#7a8fa6',
                  backgroundColor: active ? '#00bcd422' : '#111827',
                  border: `1px solid ${active ? '#00bcd4' : '#1e3a5f'}`,
                  '&:hover': { color: '#00bcd4', backgroundColor: '#00bcd411' },
                }}
              />
            );
          })}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Status:</Typography>
        {[{ val: 'all', label: 'All', color: '#c0cfe0' }, ...STATUS].map(s => {
          const active = filterStatus === s.val;
          return (
            <Chip
              key={s.val}
              label={s.label}
              size="small"
              onClick={() => setFilterStatus(s.val)}
              sx={{
                fontSize: 11, fontWeight: active ? 700 : 400, cursor: 'pointer',
                color: active ? s.color : '#7a8fa6',
                backgroundColor: active ? `${s.color}22` : '#111827',
                border: `1px solid ${active ? s.color : '#1e3a5f'}`,
                '&:hover': { color: s.color, backgroundColor: `${s.color}11` },
              }}
            />
          );
        })}
      </Box>

      {/* Column headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr 140px 28px', gap: 1.5, padding: '0 14px 6px', borderBottom: '1px solid #1e3a5f' }}>
        {['Reference', 'Requirement', 'Status', 'Ev'].map(h => (
          <Typography key={h} sx={{ color: '#7a8fa6', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</Typography>
        ))}
      </Box>

      {/* Category sections */}
      <Box sx={{ mt: 2 }}>
        {grouped.length === 0 ? (
          <Typography sx={{ color: '#4a5a70', textAlign: 'center', padding: 6, fontStyle: 'italic' }}>
            No requirements match the current filters.
          </Typography>
        ) : (
          grouped.map(([cat, list]) => (
            <CategorySection key={cat} category={cat} items={list} onSelect={setSelected} />
          ))
        )}
      </Box>

      </>)}

      <DetailDrawer item={selected} systemId={systemId} onClose={() => setSelected(null)} onSave={handleSave} canEdit={canEdit} />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{ fontSize: 13 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
